import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { DataTableType } from '@/components/DataTable/constants/dataTableConstants';
import DataTable from '@/components/DataTable/DataTable';
import { useSourceTable } from '@/components/DataTable/hooks/useDataTable';
import SourceHeader from '@/components/SourceSelection/SourceHeader';
import SourceTableFaq from '@/components/SourceSelection/SourceTableFaq';
import { tw } from '@/lib/tailwind';

/**
 * PRIMARY ROUTE: Data Source Selection
 *
 * Pure Alchemer functionality for managing and selecting data sources.
 * This route does not use ThoughtSpot - it's entirely custom Alchemer UI.
 *
 * Features:
 * - Display available data sources in a table
 * - Data source configuration and management
 * - FAQ/Help panel for data source questions
 * - Toggle FAQ visibility
 *
 * URL Pattern: /dashboard/source
 *
 * NOTE: URL changed from `/source` to `/dashboard/source` during Phase 7 refactoring
 * to consolidate all primary routes under the dashboard structure.
 *
 * Architecture Note:
 * Located in `_primary-routes/` because it doesn't use ThoughtSpot's prerendered embed.
 * This is pure Alchemer functionality for data source management.
 */
export const Route = createFileRoute('/dashboard/_primary-routes/source')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading } = context.auth;

    if (!isLoading && !isAuthenticated) {
      return; // AuthProvider will handle login redirect
    }
  },
});

function RouteComponent() {
  const [showFaqs, setShowFaqs] = useState(true);
  const sectionStyles = 'border-background-200 rounded-lg border bg-white p-4';

  return (
    <div className={tw(`flex flex-col gap-4`, `bg-background-100 px-7 py-4`)}>
      <SourceHeader close={() => setShowFaqs(true)} />
      <div
        className={tw(
          `grid`,
          `transition-all duration-500`,
          showFaqs && `grid-cols-[7fr_3fr] gap-4`,
          !showFaqs && `grid-cols-[1fr_0fr] gap-0`,
        )}
      >
        <div>
          <DataTable
            useTableHook={useSourceTable}
            tableType={DataTableType.Sources}
          />
        </div>
        <div
          className={tw(
            sectionStyles,
            `overflow-hidden transition-discrete`,
            `transition-all duration-500`,
            showFaqs && `visible opacity-100`,
            !showFaqs && `hidden opacity-0`,
          )}
        >
          <SourceTableFaq close={() => setShowFaqs(false)} />
        </div>
      </div>
    </div>
  );
}
